import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../contexts/supabase';
import SubscriptionManager from '../components/SubscriptionManager';
import ApiOnboarding from '../components/ApiOnboarding';
import ErrorBoundary from '../components/ErrorBoundary';
import LoadingSkeleton from '../components/LoadingSkeleton';
import toast from 'react-hot-toast';

function StatCard({ title, value, change, icon: Icon }) {
  const isPositive = change >= 0;
  
  return (
    <div className="bg-[#1E293B] p-6 rounded-xl border border-[#334155]">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[#94A3B8] text-sm">{title}</p>
          <p className="text-2xl font-bold mt-2 text-white">{value}</p>
        </div>
        <div className="p-3 bg-[#2563EB]/10 rounded-full">
          <Icon className="w-6 h-6 text-[#2563EB]" />
        </div>
      </div>
      <div className="mt-4 flex items-center">
        {isPositive ? (
          <ArrowUpIcon className="w-4 h-4 text-[#22C55E]" />
        ) : (
          <ArrowDownIcon className="w-4 h-4 text-red-500" />
        )}
        <span className={`ml-1 text-sm ${
          isPositive ? 'text-[#22C55E]' : 'text-red-500'
        }`}>
          {Math.abs(change)}%
        </span>
        <span className="ml-2 text-[#94A3B8] text-sm">vs last month</span>
      </div>
    </div>
  );
}

function UsageChart({ data }) {
  return (
    <div className="bg-[#1E293B] p-6 rounded-xl border border-[#334155]">
      <h3 className="text-lg font-semibold mb-6 text-white">API Usage</h3>
      <div className="h-64 flex items-end justify-between">
        {data.map((value, index) => (
          <div
            key={index}
            className="w-8 bg-gradient-to-t from-[#2563EB] to-[#4F46E5] rounded-t-lg"
            style={{ height: `${value}%` }}
          />
        ))}
      </div>
      <div className="mt-6 flex justify-between text-sm text-[#94A3B8]">
        <span>Mon</span>
        <span>Tue</span>
        <span>Wed</span>
        <span>Thu</span>
        <span>Fri</span>
        <span>Sat</span>
        <span>Sun</span>
      </div>
    </div>
  );
}

function ApiKeySection({ apiKey, onGenerateKey }) {
  return (
    <div className="bg-[#1E293B] p-6 rounded-xl border border-[#334155]">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">API Key</h3>
        <button
          onClick={onGenerateKey}
          className="px-4 py-2 bg-[#2563EB] text-white rounded-lg hover:bg-[#1D4ED8] transition-colors duration-300"
        >
          Generate New Key
        </button>
      </div>
      <div className="bg-[#0F172A] p-4 rounded-lg font-mono text-sm break-all text-[#94A3B8]">
        {apiKey}
      </div>
      <p className="mt-4 text-sm text-[#94A3B8]">
        Keep this key secure. It provides access to your account's API resources.
      </p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalRequests: '0',
    successRate: '0%',
    avgResponseTime: '0s',
    monthlyUsage: '0%',
    hasSubscription: false,
    plan: 'free',
    weeklyUsage: [0, 0, 0, 0, 0, 0, 0],
    lastUpdated: null
  });
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const refreshInterval = useMemo(() => 30000, []); // 30 seconds

  const fetchUsageStats = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data: usageData, error: usageError } = await supabase
        .from('api_usage')
        .select('*')
        .eq('user_id', user.id)
        .single();

      // If table doesn't exist, show default values
      if (usageError && usageError.code === '42P01') {
        setStats({
          totalRequests: 0,
          successRate: '100%',
          avgResponseTime: '0ms',
          monthlyUsage: '0%',
          weeklyUsage: new Array(7).fill(0),
          plan: 'free',
          hasSubscription: false,
          lastUpdated: new Date().toISOString()
        });
        return;
      }

      if (usageError) throw usageError;

      const stats = {
        totalRequests: usageData?.total_requests || 0,
        successRate: `${Math.round(usageData?.success_rate || 0)}%`,
        avgResponseTime: `${usageData?.avg_response_time || 0}ms`,
        monthlyUsage: `${usageData?.monthly_usage || 0}%`,
        weeklyUsage: usageData?.weekly_usage || new Array(7).fill(0),
        plan: usageData?.plan || 'free',
        hasSubscription: !!usageData?.has_subscription,
        lastUpdated: new Date().toISOString()
      };

      setStats(stats);
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      // Only show error toast if it's not a missing table error
      if (error.code !== '42P01') {
        toast.error('Failed to fetch usage statistics');
      }
      setError('Failed to fetch usage statistics');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        setLoading(true);
        setError(null);
        
        if (!user) {
          setError('Please sign in to view your dashboard');
          return;
        }
        
        // Fetch user's API key
        const { data: apiKeys, error: apiKeyError } = await supabase
          .from('api_keys')
          .select('key_hash')
          .eq('user_id', user.id)
          .eq('active', true)
          .single();
          
        if (apiKeyError && apiKeyError.code !== 'PGRST116') {
          console.error('API key fetch error:', apiKeyError);
          throw apiKeyError;
        }

        const hasKey = !!apiKeys?.key_hash;
        setApiKey(apiKeys?.key_hash || 'No API key generated yet');
        
        // Check if this is a new API key
        const { data: keyHistory } = await supabase
          .from('api_keys')
          .select('created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);
          
        // Show onboarding if this is their first key or it was just created (within last minute)
        if (hasKey && keyHistory?.[0]) {
          const keyAge = Date.now() - new Date(keyHistory[0].created_at).getTime();
          setShowOnboarding(keyAge < 60000); // Show if key is less than 1 minute old
        }

        // Fetch initial usage stats
        await fetchUsageStats();
        
        // Set up auto-refresh interval
        const interval = setInterval(fetchUsageStats, refreshInterval); // Refresh every 30 seconds
        return () => clearInterval(interval);
        
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        setError(err.message || 'Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      fetchDashboardData();
    }

    // Cleanup interval on unmount
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user, fetchUsageStats, refreshInterval]);

  const handleGenerateNewKey = async () => {
    try {
      // Call our new API key generation function
      const { data, error } = await supabase
        .rpc('generate_api_key', {
          user_id: user.id
        });

      if (error) {
        if (error.message.includes('No active subscription')) {
          setError('You need an active subscription to generate an API key. Please subscribe to a plan first.');
        } else {
          throw error;
        }
        return;
      }

      setApiKey(data);
      setShowOnboarding(true);
      toast.success('New API key generated successfully');
      
    } catch (err) {
      console.error('API key generation error:', err);
      toast.error('Failed to generate new API key');
      setError('Failed to generate new API key. Please try again later.');
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F172A] pt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-[#1E293B] p-6 rounded-xl border border-red-500/20">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Error Loading Dashboard</h3>
                  <p className="text-[#94A3B8] mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-[#2563EB] text-white rounded-lg hover:bg-[#1D4ED8] transition-colors duration-300"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] pt-24">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-white"
          >
            Dashboard
          </motion.h1>
          {stats.plan && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 flex items-center space-x-4"
            >
              <p className="text-blue-400">
                {stats.plan.charAt(0).toUpperCase() + stats.plan.slice(1)} Plan
              </p>
              {stats.lastUpdated && (
                <div className="flex items-center text-[#94A3B8] text-sm">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  Last updated: {new Date(stats.lastUpdated).toLocaleTimeString()}
                </div>
              )}
            </motion.div>
          )}
          {!stats.hasSubscription && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
            >
              <p className="text-yellow-500">
                ⚠️ You don't have an active subscription. 
                <a href="/pricing" className="ml-2 underline hover:text-yellow-400">
                  Subscribe now
                </a> to generate an API key and start using our services.
              </p>
            </motion.div>
          )}
        </div>

        {/* Main Content */}
        <ErrorBoundary>
          <div className="grid grid-cols-1 gap-8">
            {showOnboarding ? (
              <ApiOnboarding apiKey={apiKey} />
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <ErrorBoundary>
                    <StatCard
                      title="Total Requests"
                      value={stats.totalRequests}
                      change={15}
                      icon={ChartBarIcon}
                    />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <StatCard
                      title="Success Rate"
                      value={stats.successRate}
                      change={5}
                      icon={CheckCircleIcon}
                    />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <StatCard
                      title="Avg Response Time"
                      value={stats.avgResponseTime}
                      change={-10}
                      icon={ClockIcon}
                    />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <StatCard
                      title="Monthly Usage"
                      value={stats.monthlyUsage}
                      change={25}
                      icon={ChartBarIcon}
                    />
                  </ErrorBoundary>
                </div>

                {/* API Key Section */}
                <ErrorBoundary>
                  <ApiKeySection apiKey={apiKey} onGenerateKey={handleGenerateNewKey} />
                </ErrorBoundary>

                {/* Usage Chart */}
                <ErrorBoundary>
                  <UsageChart data={stats.weeklyUsage} />
                </ErrorBoundary>

                {/* Subscription Management */}
                <ErrorBoundary>
                  <SubscriptionManager />
                </ErrorBoundary>
              </>
            )}
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
}
